import { createOAuthStateForKintone } from '../../util/kintone/index';
import { defaultPastDate, isKintoneTenantSetting, kintoneSetting, type LinkServiceTokenExpiredDate, linkServiceTokenExpiredDate, registerOAuthState } from './api';
import { getTenantSettingContentById, getUserSettingContentId, ServiceName, type settingContent, tenantSettingId, userSettingId } from './settingContents';

import Log from '@/Logger';

/**
 * 呼び出したユーザーのサービス利用状況を設定し、それぞれのサービスの文言を入れたUserSettingMessageの配列を返す
 */
export async function getTenantSetting(isChangedOn: boolean, isRetry: boolean): Promise<settingContent[]> {
    //サービスごとに設定を配列に入れていく
    let userSetting: settingContent[] = []

    //kintoneのテナント設定(管理者設定)を繰り返し取得し配列にプッシュ
    for (const id of Object.values(tenantSettingId)) {
        const tenantSetting = await getStateTenantSetting(id, isChangedOn, isRetry)
        userSetting.push(tenantSetting)
    }
    return userSetting

};

async function getStateTenantSetting(id: string, isChangedOn: boolean, isRetry: boolean): Promise<settingContent> {
    const settingMessage = getTenantSettingContentById(id)
    if (!settingMessage) {
        throw new Error(`SettingMessage ${id} is not found`)
    }

    let isEnabled: boolean
    //オンオフの判定が複雑な場合は条件分岐で都度処理を追加する
    switch (id) {
        case tenantSettingId.kintone:
            isEnabled = await fetchWithRetryKintoneTenantSetting(isChangedOn, isRetry)
            break;

        default:
            isEnabled = settingMessage?.isSettingEnabledFunction ? await settingMessage.isSettingEnabledFunction() : false
            break;
    }

    const tenantSetting = settingMessage;
    tenantSetting.state = isEnabled ? 'ENABLED' : 'DISABLED';

    return tenantSetting
}

/**
 * ログインユーザーの設定一覧の配列を返す
 * 現状はkintoneのみ
 */
export async function getUserSetting(isRetry: boolean): Promise<settingContent[]> {
    const userSetting: settingContent[] = []

    for (const id of Object.values(userSettingId)) {
        const eachSetting = await getEachUserSetting(id, isRetry)
        if (eachSetting) {
            userSetting.push(eachSetting)
        }
    }

    return userSetting
}

async function getEachUserSetting(id: string, isRetry: boolean): Promise<settingContent | null> {
    const logger = Log.getLogger();

    const settingMessage = getUserSettingContentId(id)
    if (!settingMessage) {
        logger.ui.error('LinkServiceSetting', 'getEachUserSetting', `SettingMessage ${id} is not found`)
        return null;
    }

    //オンオフの判定が複雑な場合は条件分岐で都度処理を追加する
    let isEnabled: Boolean
    switch (id) {
        case userSettingId.kintone://リトライ分岐が生じるため個別で処理を行う
            const expiredDate = await fetchWithRetryKintoneToken(isRetry)
            isEnabled = isExpired(expiredDate.refreshTokenExpiredDate)
            break;

        default://複雑でない場合にはsettingContent内で定義される関数で判定を行う
            isEnabled = settingMessage?.isSettingEnabledFunction ? await settingMessage.isSettingEnabledFunction() : false;
    }

    const userSetting = settingMessage;
    userSetting.state = isEnabled ? 'ENABLED' : 'DISABLED';

    return userSetting;
}

function isExpired(expiredDate: string): Boolean {
    const logger = Log.getLogger();

    const now = new Date(); // 現在日時
    const expiryDate = new Date(expiredDate); // expiredDate を Date オブジェクトに変換
    if (isNaN(expiryDate.getTime())) {
        logger.ui.error('LinkServiceSetting', 'isExpired', 'Invalid date format');
        return false;
    }

    return now > expiryDate ? false : true;
}

/**
 * 外部サービスの認可ページに遷移させる。
 * 遷移するため返り値は無し。
 * @param id 
 */
export async function initiateOAuth(id: string): Promise<void> {
    switch (id) {
        case userSettingId.kintone:
            await initiateCybozuAuth();
            break;
        default:
            throw new Error('Invalid key');
    }
    return;
}

/**
 * kintoneの画面に遷移して認証させる関数。
 * kintoneのテナント設定をDBから取得し、遷移先を決める
 * 遷移の際にuuidを発行し、state値としてURLに載せる。認可後にuuidをチェックする。
 */
async function initiateCybozuAuth(): Promise<void> {
    try {
        //サービス連携テーブルのテナント設定情報を取得する
        const kintoneTenantSetting = await kintoneSetting()

        const clientId = kintoneTenantSetting.clientId;
        const redirectUri = window.location.origin;
        const tokenUrl = kintoneTenantSetting.endpointUrl;

        const urlObj = new URL(tokenUrl)
        urlObj.pathname = '/oauth2/authorization'
        const urlAuth = urlObj.toString()

        //stateの値をもとに、認可後に戻ってきたリクエストが正常かを判定する
        const state = createOAuthStateForKintone()

        //作成したstateをDBに送る
        await registerOAuthState(state, ServiceName.kintone)

        const scopes = 'k:app_settings:read%20k:app_record:write'//スコープはハードコードする
        const url = `${urlAuth}?client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&response_type=code&scope=${scopes}`

        // CybozuのOAuth認証ページにリダイレクト
        window.location.href = url;
        return;
    } catch (err) {
        throw new Error('Failed to initiate Cybozu authentication');
    }
};

async function fetchWithRetryKintoneToken(isRetry: boolean): Promise<LinkServiceTokenExpiredDate> {
    const logger = Log.getLogger();

    const maxRetries = 3;//リトライ回数の上限
    const baseDelay = 1000; // ベースとなる待機時間（ms）
    try {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            const response: LinkServiceTokenExpiredDate = await linkServiceTokenExpiredDate(ServiceName.kintone);
            if (!isRetry) {//リトライフラグが無効なら即返す
                return response
            }

            // レコードに有効なレスポンスがあれば即返す
            if (response !== defaultPastDate) {
                return response;
            }

            // 最大リトライ回数に達した場合は過去の日付けを返す
            if (attempt === maxRetries) {
                logger.ui.error('LinkServiceSetting', 'fetchWithRetry', 'Max retries reached');
                return defaultPastDate;
            }

            // エクスポネンシャルバックオフ。最大8s(2^3)待機を行う
            const delay = baseDelay * Math.pow(2, attempt);
            logger.ui.info('LinkServiceSetting', 'fetchWithRetry', `API call retry linkServiceTokenExpiredDate count: ${attempt + 1}, wait: ${delay}ms`)
            await new Promise(resolve => setTimeout(resolve, delay));
        }
        return defaultPastDate

    } catch (err) {//不明なエラーの場合は過去の日付けを返す
        return defaultPastDate
    }
}

async function fetchWithRetryKintoneTenantSetting(isChangedOn: boolean, isRetry: boolean): Promise<boolean> {
    const logger = Log.getLogger();

    const maxRetries = 3;//リトライ回数の上限
    const baseDelay = 1000; // ベースとなる待機時間（ms）
    try {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            const response: boolean = await isKintoneTenantSetting();
            if (!isRetry) {//リトライフラグが無効なら即返す
                return response;
            }

            if (response === isChangedOn) {
                return response;
            }

            // 最大リトライ回数に達した場合は過去の日付けを返す
            if (attempt === maxRetries) {
                logger.ui.info('LinkServiceSetting', 'fetchWithRetryKintoneTenantSetting', 'Max retries reached');
                return false;
            }

            // エクスポネンシャルバックオフ。最大8s(2^3)待機を行う
            const delay = baseDelay * Math.pow(2, attempt);
            logger.ui.info('LinkServiceSetting', 'fetchWithRetry', `API call retry fetchWithRetryKintoneTenantSetting count: ${attempt + 1}, wait: ${delay}ms`)
            await new Promise(resolve => setTimeout(resolve, delay));
        }
        return false

    } catch (err) {//不明なエラーの場合は過去の日付けを返す
        return false
    }
}